import { Hero } from '../components/landing/marketing/Hero';
import { SocialProof } from '../components/landing/marketing/SocialProof';
import { TrustBar } from '../components/landing/marketing/TrustBar';
import { BuiltForLocalServices } from '../components/landing/marketing/BuiltForLocalServices';
import { SEO } from '../components/landing/shared/SEO';
import * as Sentry from '@sentry/react';
import { MotionConfig } from 'framer-motion';

// Eager imports for all 8 below-fold sections (D-05: lazy imports prevent
// sections from appearing in prerendered HTML — Suspense fallback renders instead).
// The browser bundle remains route-split via App.tsx; this page itself is small.
import { Features } from '../components/landing/marketing/Features';
import { FeatureRows } from '../components/landing/marketing/FeatureRows';
import { LiveDemo } from '../components/landing/marketing/LiveDemo';
import { HowItWorks } from '../components/landing/marketing/HowItWorks';
import { Pricing } from '../components/landing/marketing/Pricing';
import { FAQ } from '../components/landing/marketing/FAQ';
import { Cta } from '../components/landing/marketing/Cta';
import { Footer } from '../components/landing/layout/Footer';

export default function LandingPage() {
  return (
    <MotionConfig reducedMotion="user">
    <Sentry.ErrorBoundary fallback={<div className="p-8 text-center text-red-500">Something went wrong loading this page. Please try refreshing.</div>}>
      <SEO
        title="ChytBot - AI Chatbot for Customer Service | Free AI Chatbot Builder"
        description="Build a free AI chatbot for customer service. ChytBot's conversational AI answers customer questions online 24/7, trained on your own docs. No coding required."
        url="https://chytbot.com"
        keywords={[
          // Primary keywords
          "chatbot",
          "AI chatbot",
          "ai chat",
          "ai bot",
          "chatbot ai",
          "AI chatbot builder",
          "chatbot for business",
          "customer support chatbot",
          "chatbot customer service",
          "automated customer service",
          "conversational AI",
          "artificial intelligence chat",
          "best AI chatbot",

          // US-focused keywords
          "AI chatbot USA",
          "chatbot for US businesses",
          "American business automation",
          "customer service automation USA",
          "US small business chatbot",

          // Use case keywords
          "ecommerce chatbot",
          "website chatbot",
          "chatbot online",
          "AI chatbot online",
          "AI chat online",
          "chatbot live",
          "live chat automation",
          "24/7 customer support",
          "no code chatbot builder",
          "talk to AI",

          // Free-tier keywords
          "free AI chatbot",
          "AI chatbot free",
          "free chatbot",
          "chatbot free",
          "free AI chat",
          "AI chat free",
          "chatbot free online",
          "chat bot online free",

          // GPT-related keywords
          "chatbot GPT",
          "GPT chatbot",
          "AI chat GPT",
          "AI chatbot GPT",

          // Competitor alternatives
          "Intercom alternative",
          "Intercom chatbot alternative",
          "Drift alternative",
          "Drift chatbot alternative",
          "Zendesk alternative",
          "Zendesk chatbot alternative",
          "HubSpot chatbot alternative",
          "Ada chatbot alternative",
          "Dialogflow chatbot alternative",
          "affordable chatbot",

          // Long-tail keywords
          "how to add chatbot to website",
          "best chatbot for small business",
          "AI customer service bot",
          "AI chat bots",
          "automated chat support",

          // Brand
          "ChytBot",
        ]}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": "https://chytbot.com#website",
            "name": "ChytBot",
            "url": "https://chytbot.com",
            "description": "AI Chatbot Platform for US Businesses",
            "publisher": {
              "@type": "Organization",
              "name": "ChytBot"
            },
            "inLanguage": "en-US",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://chytbot.com/?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "ChytBot AI Chatbot Service",
            "description": "AI-powered chatbot platform that helps American businesses automate customer support",
            "provider": {
              "@type": "Organization",
              "name": "ChytBot"
            },
            "areaServed": {
              "@type": "Country",
              "name": "United States"
            },
            "serviceType": "Customer Service Automation",
            "category": "Business Software"
          }
        ]}
      />
      {/* Section order: Hero → SocialProof → TrustBar → Features → FeatureRows → LiveDemo → HowItWorks → BuiltForLocalServices → Pricing → FAQ → Cta → Footer */}
      <div className="aceternity-page-shell relative overflow-hidden">
        <div className="aceternity-grid-mask pointer-events-none absolute inset-x-0 top-0 h-[900px] opacity-60" aria-hidden="true" />
        {/* Hero + TrustBar: above-fold LCP surfaces — always eager (PERF-04) */}
        <Hero />
        <SocialProof />
        <TrustBar />
        {/* Below-fold sections: eager imports (D-05) — required for prerendered HTML to contain full marketing content */}
        <Features />
        <FeatureRows />
        <LiveDemo />
        <HowItWorks />
        <BuiltForLocalServices />
        <Pricing />
        <FAQ />
        <Cta />
        <Footer />
      </div>
    </Sentry.ErrorBoundary>
    </MotionConfig>
  );
}
